import { useEffect, useState } from "react";
import "../styles/RegisterPage.css";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import { RegisterPanel } from "./RegisterPanel";
import { IndexHeaderNav } from "./IndexHeaderNav";
import * as Sentry from '@sentry/react';

function RegisterComplete() {
    const [firstWait, setFirstWait] = useState(false);
    const [lockedEmail, setLockedEmail] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [adminReg, setAdminReg] = useState(null);
    const [authType, setAuthType] = useState("password");
    const [prefillFirstName, setPrefillFirstName] = useState("");
    const [prefillLastName, setPrefillLastName] = useState("");


    const navigate = useNavigate();
    const { theme } = useTheme();

    const [needsCode, setNeedsCode] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const withCodeParam = params.get("withCode"); // "True" | "False" | null
    const hasChosen = withCodeParam === "True" || withCodeParam === "False";

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant", // or "smooth" if you want
        });
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setAdminReg(params.get("type") === "admin");
    }, []);

    // Fetch the stored email for Step 2
    useEffect(() => {
        if (adminReg === null) return;

        const endpoint = adminReg ? "/api/get_admin_register_email" : "/api/get_register_email";

        (async () => {
            try {
                const response = await fetch(endpoint, { method: "GET" });
                const data = await response.json();

                if (!response.ok) {
                    setErrorMessage(data.error_message || 'Failed to load. Please try again.');
                    return;
                }

                if (data.redirect) {
                    window.location.href = data.redirect;
                    return;
                }

                if (!adminReg && data.colors && typeof data.colors.needs_code === "boolean") {
                    setNeedsCode(data.colors.needs_code);
                }

                if (data.email) {
                    setLockedEmail(data.email);
                } else {
                    setErrorMessage("Missing email. Please restart registration.");
                }
                if (data.auth_type) {
                    setAuthType(data.auth_type);
                }
                if (data.first_name) {
                    setPrefillFirstName(data.first_name);
                }
                if (data.last_name) {
                    setPrefillLastName(data.last_name);
                }
            } catch (e) {
                Sentry.captureException(e);
                setErrorMessage('Network error. Please try again.');
            } finally {
                setFirstWait(true);
            }
        })();
    }, [adminReg]);

    const headerTitle = adminReg ? "Admin Register" : "Complete Registration";

    return (
        <div className="register-page">
            <IndexHeaderNav />
            <div className="register-shell">
                <div className="register-card">
                    <div className="register-header">
                        <div className="register-header-left">
                            <button
                                className="back-btn"
                                type="button"
                                onClick={() => {
                                    if (adminReg) {
                                        navigate("/register?type=admin");
                                    } else {
                                        navigate("/register");
                                    }
                                }}
                            >
                                ← Back
                            </button>
                        </div>

                        <div className="register-header-center">
                            <h1>{headerTitle}</h1>
                        </div>

                        <div className="register-header-right">
                            {adminReg && (
                                <img
                                    src={theme.logo}
                                    alt="Rezify Logo"
                                    className="admin-logo"
                                />
                            )}
                        </div>
                    </div>

                    {!firstWait && (
                        <div className="center-row">
                            <div className="spinner-black" />
                        </div>
                    )}

                    {firstWait && (
                        <>
                            {errorMessage.length >= 1 && (
                                <div className="error-box">
                                    {errorMessage}
                                </div>
                            )}

                            {!adminReg && needsCode && !hasChosen ? (
                                <div className="register-code-choice">
                                    <button
                                        className="primary-btn"
                                        type="button"
                                        onClick={() => navigate("?withCode=True", { replace: true })}
                                    >
                                        Register with Class Code
                                    </button>

                                    <button
                                        className="primary-btn secondary"
                                        type="button"
                                        onClick={() => navigate("?withCode=False", { replace: true })}
                                    >
                                        Continue without Code
                                    </button>
                                </div>
                            ) : (
                                <RegisterPanel
                                    lockedEmail={lockedEmail}
                                    authType={authType}
                                    prefillFirstName={prefillFirstName}
                                    prefillLastName={prefillLastName}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export { RegisterComplete };
